import { useCallback, useEffect, useRef } from "react";
import api from "@/lib/api";

const HEARTBEAT_INTERVAL_MS = 15_000;
const INPUT_FRESHNESS_MS = 30_000; // we ourselves consider "active" within 30s
// Generate a per-tab session token. localStorage would be SHARED across tabs
// (defeating the multi-tab guard); sessionStorage is keyed to the tab AND
// survives intra-tab reloads. Exactly what we want.
function tabSessionToken() {
    let t = sessionStorage.getItem("lesson_session_token");
    if (!t) {
        t = "tab_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem("lesson_session_token", t);
    }
    return t;
}

/**
 * useFocusedTimeHeartbeat
 *
 * Emits one heartbeat every 15s to /api/lessons/{lessonId}/heartbeat while
 * the lesson is being viewed. Heartbeats are only sent when:
 *   - the tab is visible (document.visibilityState === "visible")
 *   - the user has interacted within the last `INPUT_FRESHNESS_MS`
 *   - the lessonId is truthy and `enabled` is true
 *
 * The server applies its own guards too (rate limit, freshness, token
 * rotation), so this hook can stay focused on UX (don't waste requests
 * while the tab is hidden / user is AFK).
 *
 * @param {string|null} lessonId        active lesson id
 * @param {boolean}     enabled         master switch (e.g. lesson is open)
 * @param {function}    onCredit        optional `(payload)` callback after a
 *                                      credited heartbeat — drives the in-UI
 *                                      time bar
 */
export default function useFocusedTimeHeartbeat(lessonId, enabled, onCredit) {
    const lastInputRef = useRef(Date.now());
    const inFlightRef = useRef(false);

    // Track latest input time. Throttled to ~1Hz via the timestamp itself
    // (no need for explicit debounce; we just record the most-recent stamp).
    useEffect(() => {
        if (!enabled) return undefined;
        const onInput = () => { lastInputRef.current = Date.now(); };
        const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
        events.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
        return () => events.forEach((e) => window.removeEventListener(e, onInput));
    }, [enabled]);

    const sendHeartbeat = useCallback(async () => {
        if (!lessonId || !enabled) return;
        if (inFlightRef.current) return;
        if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
        const lastInputAgoMs = Date.now() - lastInputRef.current;
        if (lastInputAgoMs > INPUT_FRESHNESS_MS) return;

        inFlightRef.current = true;
        try {
            const { data } = await api.post(`/lessons/${lessonId}/heartbeat`, {
                tab_visible: true,
                last_input_ago_seconds: Math.round(lastInputAgoMs / 1000),
                session_token: tabSessionToken(),
                interval_seconds: Math.round(HEARTBEAT_INTERVAL_MS / 1000),
            });
            if (onCredit && data && data.reason === "credited") onCredit(data);
        } catch (err) {
            // 401/403 means the session expired — stop trying silently.
            if (err?.response?.status === 401 || err?.response?.status === 403) {
                // no-op; next visibility-change will re-evaluate
            }
        } finally {
            inFlightRef.current = false;
        }
    }, [lessonId, enabled, onCredit]);

    // Heartbeat tick
    useEffect(() => {
        if (!lessonId || !enabled) return undefined;
        const id = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
        // Fire one immediately when the lesson becomes active.
        const initial = setTimeout(sendHeartbeat, 1500);
        return () => { clearInterval(id); clearTimeout(initial); };
    }, [lessonId, enabled, sendHeartbeat]);

    // Send a final beat when the tab becomes visible again (catches the
    // common "switch back" case so the bar reflects accurate state).
    useEffect(() => {
        if (!enabled) return undefined;
        const onVis = () => {
            if (document.visibilityState === "visible") sendHeartbeat();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [enabled, sendHeartbeat]);
}
